import type { NavigationLinkProps } from './NavigationLink';
import NavigationLink from './NavigationLink';
import { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

import UserBanner from './UserBanner';
// @ts-ignore
import { type UserBannerProps } from './UserBanner';

export const NavLinkHeaders = {
  HOME: 1,
  ABOUTUS: 2,
  CLIENTCENTER: 3,
  SALESFAQ: 4,
  TERRASTATION: 5,
  PRIVACY: 6,
  NONE: 7
} as const;

export type NavLinkHeaders =
  (typeof NavLinkHeaders)[keyof typeof NavLinkHeaders];

/* 
<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path  d="m12 5.69l5 4.5V18h-2v-6H9v6H7v-7.81zM12 3L2 12h3v8h6v-6h2v6h6v-8h3z"/></svg>
*/


function get_default_links(): NavigationLinkProps[] {
  const props: NavigationLinkProps[] = [];

  // Home Link
  props.push({
    baseLinkURL: {
      linkText: 'Dashboard',
      linkURL: '/'
    }
  });

  props.push({
    baseLinkURL: {
      linkText: 'Contact Us',
      linkURL: '/contact-form'
    },
    dropdownLinks: [
      {
        linkText: 'Contact Form',
        linkURL: '/contact-form'
      },
      {
        linkText: 'Contact Info',
        linkURL: '/contact-info'
      }
    ]
  });

  props.push({
    baseLinkURL: {
      linkText: 'Client Center',
      linkURL: '/client-center'
    },
    dropdownLinks: [
      {
        linkText: 'Newsletters',
        linkURL: '/client-center/newsletters'
      },
      {
        linkText: 'Useful Reading',
        linkURL: '/client-center/useful-reading'
      },
      {
        linkText: 'Related Articles',
        linkURL: '/client-center/related-articles'
      },
      {
        linkText: 'Links',
        linkURL: '/client-center/links'
      }
    ]
  });

  props.push({
    baseLinkURL: {
      linkText: 'Sales FAQ',
      linkURL: '/sales-faq'
    }
  });

  props.push({
    baseLinkURL: {
      linkText: 'TerraStation',
      linkURL: '/terrastation'
    },
    dropdownLinks: [
      {
        linkText: 'TerraStation Fliers',
        linkURL: '/terrastation/terrastation-fliers'
      },
      {
        linkText: 'Tech Sheets',
        linkURL: '/terrastation/tech-sheets'
      }
    ]
  });

  props.push({
    baseLinkURL: {
      linkText: 'Privacy',
      linkURL: '/privacy'
    },
    dropdownLinks: [
      {
        linkText: 'Privacy Policy',
        linkURL: '/privacy/privacy-policy'
      },
      {
        linkText: 'Terms of Use',
        linkURL: '/privacy/terms-of-use'
      }
    ]
  });

  return props;
}

const DEFAULTNAVPROPS = get_default_links();

interface NavigationProps {
  activeHeader?: NavLinkHeaders;
}

function Navigation(props: NavigationProps) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const navRef = useRef<HTMLElement>(null);

  const location = useLocation();
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!mobileOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMobileOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [mobileOpen]);

  useEffect(() => {
    if (!mobileOpen) return;
    const onPointer = (e: PointerEvent) => {
      if (navRef.current && !navRef.current.contains(e.target as Node)) {
        setMobileOpen(false);
      }
    };
    document.addEventListener('pointerdown', onPointer);
    return () => document.removeEventListener('pointerdown', onPointer);
  }, [mobileOpen]);

  // update active
  if (props.activeHeader) {
    let index = props.activeHeader - 1;
    if (index >= 0 && index < DEFAULTNAVPROPS.length) {
      DEFAULTNAVPROPS.forEach((navProp) => {
        navProp.isActive = false;
      });
      DEFAULTNAVPROPS[index].isActive = true;
    }
  }

  let userBannerProps: UserBannerProps = {
    isSignedIn: !!localStorage.getItem('ts_signedin') || false,
    username: localStorage.getItem('ts_username') || 'Guest'
  };

  return (
    <nav
      ref={navRef}
      id="ts-page-nav"
      className="fixed w-full z-50 bg-light-bg shadow-sm"
    >
      {/* ── top bar ──────────────────────────────────────────────────────────── */}
      <div
        id="ts-nav-wrapper"
        className="max-w-7xl mx-auto p-4 flex items-center justify-between"
      >
        {/* Logo */}
        <div className="flex items-center">
          <a href="https://terrasciences.com">
            <img
              className="h-10 w-auto"
              src="/icons/tsgtkicon128.png"
              alt="TerraSciences Logo"
            />
          </a>
        </div>

        {/* Desktop: nav links */}
        <div className="hidden xl:flex space-x-8 flex-row">
          {DEFAULTNAVPROPS.map((linkProps, i) => (
            <NavigationLink key={i} {...linkProps} />
          ))}
        </div>

        {/* Desktop: user banner */}
        <div className="hidden xl:flex flex-row">
          <UserBanner key={1} {...userBannerProps} />
        </div>

        {/* Desktop: socials */}
        <div
          id="socials-banner"
          className="hidden xl:flex flex-row items-center justify-center"
        >
          <div className="mx-2">
            <a
              href="https://www.linkedin.com/company/terrasciences/"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img
                src="/icons/logo-linkedin.svg"
                alt="LinkedIn"
                className="h-6 w-auto"
              />
            </a>
          </div>
        </div>

        {/* Mobile: hamburger / close button */}
        <button
          type="button"
          className="xl:hidden inline-flex items-center justify-center p-2 rounded-md
                   text-light-text hover:bg-light-border
                   focus:outline-none focus:ring-2 focus:ring-inset focus:ring-light-main/40
                   transition-colors duration-150"
          aria-expanded={mobileOpen}
          aria-controls="ts-mobile-menu"
          aria-label={
            mobileOpen ? 'Close navigation menu' : 'Open navigation menu'
          }
          onClick={() => setMobileOpen((o) => !o)}
        >
          <svg
            className="h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
            aria-hidden="true"
          >
            {mobileOpen ? (
              /* X / close */
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6 18L18 6M6 6l12 12"
              />
            ) : (
              /* Hamburger */
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M4 6h16M4 12h16M4 18h16"
              />
            )}
          </svg>
        </button>
      </div>

      {/* mobile dropdown */}
      {mobileOpen && (
        <div
          id="ts-mobile-menu"
          className="xl:hidden border-t border-light-border bg-light-bg shadow-md"
        >
          {/* Nav links — stacked vertically */}
          <div className="px-4 py-2 flex flex-col divide-y divide-light-border">
            {DEFAULTNAVPROPS.map((linkProps, i) => (
              <div key={i} className="py-2">
                <NavigationLink {...linkProps} mobile />
              </div>
            ))}
          </div>

          {/* User banner */}
          <div className="border-t border-light-border px-4 py-3">
            <UserBanner {...userBannerProps} />
          </div>

          {/* Socials */}
          <div className="border-t border-light-border px-4 py-3">
            <a
              href="https://www.linkedin.com/company/terrasciences/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-light-text
                       hover:opacity-75 focus:outline-none focus:ring-2
                       focus:ring-light-main/40 rounded transition-opacity"
            >
              <img
                src="/icons/logo-linkedin.svg"
                alt=""
                aria-hidden="true"
                className="h-5 w-auto"
              />
              <span className="text-sm font-medium">LinkedIn</span>
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}

export default Navigation;
